import type {
  CustomFilter,
  CustomFilterCondition,
  DataBuilderCustomColumn,
  DataBuilderSettingValue,
  DatasetInstance,
  DatasetInstanceConfig,
  DeliveryMethod,
  ModelVersionSelections,
  S3DeliveryConfig,
  SelectionListSelectedItem,
  SftpDeliveryConfig,
  SnowflakeDeliveryConfig,
} from '../data-builder.store';
import {
  buildDatasetInstanceLabelMap,
  buildDatasetLabelMap,
} from '../datasets/dataset-instance-labels';
import type {
  DataBuilderAndFilter,
  DataBuilderBooleanFilterInput,
  DataBuilderColumnsWithCustom,
  DataBuilderCompanyMapper,
  DataBuilderCompanySets,
  DataBuilderDateFilterInput,
  DataBuilderDeliverable,
  DataBuilderDeliverableGroup,
  DataBuilderDeliverableInput,
  DataBuilderDeliverableSetting,
  DataBuilderDelivery,
  DataBuilderDownloadDelivery,
  DataBuilderFileInput,
  DataBuilderFormat,
  DataBuilderModelVersions,
  DataBuilderNumericFilterInput,
  DataBuilderOrFilter,
  DataBuilderPayload,
  DataBuilderSchema,
  DataBuilderSelectionFilter,
  DataBuilderStringFilterInput,
  DataBuilderUnitOfAnalysisInput,
  DataBuilderUnitOfAnalysisSelectionInput,
} from './data-builder-api.types';

type ColumnKeyOption = {
  key: string;
  isDefault: boolean;
  isRequired: boolean;
};

function getRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function flattenColumnKeyOptions(items: unknown): ColumnKeyOption[] {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.flatMap((item) => {
    const record = getRecord(item);
    if (record === null) {
      return [];
    }

    if (record.type === 'column' && typeof record.key === 'string') {
      return [
        {
          key: record.key,
          isDefault: record.default === true,
          isRequired: record.required === true,
        },
      ];
    }

    if (record.type === 'group' || record.type === 'set') {
      return flattenColumnKeyOptions(record.columns);
    }

    return [];
  });
}

function getColumnKeyOptions(columnsBlock: unknown): ColumnKeyOption[] {
  return flattenColumnKeyOptions(getRecord(columnsBlock)?.columns);
}

function orderPresetColumnKeys(
  selectedKeys: string[],
  columnOptions: ColumnKeyOption[]
): string[] {
  const selectedSet = new Set(selectedKeys);
  const orderedKeys = columnOptions
    .filter((option) => selectedSet.has(option.key))
    .map((option) => option.key);
  const orderedKeySet = new Set(orderedKeys);
  const unknownKeys = selectedKeys.filter((key) => !orderedKeySet.has(key));
  return [...orderedKeys, ...unknownKeys];
}

function isDeliverableGroup(
  item: DataBuilderDeliverable | DataBuilderDeliverableGroup
): item is DataBuilderDeliverableGroup {
  return item.type === 'group';
}

function flattenDeliverables(
  items: (DataBuilderDeliverable | DataBuilderDeliverableGroup)[]
): DataBuilderDeliverable[] {
  const out: DataBuilderDeliverable[] = [];
  for (const item of items) {
    if (isDeliverableGroup(item)) {
      out.push(...item.deliverables);
    } else {
      out.push(item);
    }
  }
  return out;
}

export type BuildPayloadState = {
  datasetInstances: DatasetInstance[];
  configByInstance: Record<string, DatasetInstanceConfig>;
  lagData: number;
  deliveryMethod: DeliveryMethod;
  deliveryFormat: DataBuilderFormat;
  snowflakeConfig: SnowflakeDeliveryConfig | null;
  s3Config: S3DeliveryConfig | null;
  sftpConfig: SftpDeliveryConfig | null;
  modelVersions: ModelVersionSelections;
};

export type BuildPayloadInput = {
  schema: DataBuilderSchema;
  state: BuildPayloadState;
  client: string | null;
  name: string;
  datasets?: { id: string; label: string }[];
};

function buildDelivery(state: BuildPayloadState): DataBuilderDelivery {
  if (state.deliveryMethod === 'snowflake') {
    if (state.snowflakeConfig === null) {
      throw new Error('buildDelivery: snowflakeConfig is required');
    }
    return {
      type: 'snowflake',
      database: state.snowflakeConfig.database,
      schema: state.snowflakeConfig.schema,
      table: state.snowflakeConfig.table,
    };
  }
  if (state.deliveryMethod === 's3') {
    if (state.s3Config === null) {
      throw new Error('buildDelivery: s3Config is required');
    }
    return {
      type: 's3',
      bucket: state.s3Config.bucket,
      path: state.s3Config.path,
      format: state.s3Config.format,
    };
  }
  if (state.deliveryMethod === 'sftp') {
    if (state.sftpConfig === null) {
      throw new Error('buildDelivery: sftpConfig is required');
    }
    return {
      type: 'sftp',
      path: state.sftpConfig.path,
      format: state.sftpConfig.format,
    };
  }
  const downloadDelivery: DataBuilderDownloadDelivery = {
    type: 'download',
    format: state.deliveryFormat,
  };
  return downloadDelivery;
}

function buildAggregation(
  deliverable: DataBuilderDeliverable,
  columnKeysByBreakdown: Record<string, string[]>,
  customColumnsByBreakdown: Record<string, DataBuilderCustomColumn[]>
): DataBuilderColumnsWithCustom | null {
  const columnOptions = getColumnKeyOptions(deliverable.aggregation_columns);
  const aggKeys = columnKeysByBreakdown['aggregation'] ?? [];
  const custom = toApiCustomColumns(
    customColumnsByBreakdown['aggregation'] ?? []
  );
  if (aggKeys.length === 0 && custom === null) {
    return null;
  }
  return {
    preset: orderPresetColumnKeys(aggKeys, columnOptions),
    custom,
  };
}

function buildCompanyUnitOfAnalysisValue(
  companyMapper: DataBuilderCompanyMapper | null,
  companySets: DataBuilderCompanySets['company_sets'] | null
): DataBuilderCompanySets | DataBuilderCompanyMapper {
  if (companyMapper !== null) {
    return {
      type: 'company_mapper',
      pipeline_input: companyMapper.pipeline_input,
      company_reference: companyMapper.company_reference,
      ...(companyMapper.selection_type !== undefined &&
      companyMapper.selection_type !== null
        ? { selection_type: companyMapper.selection_type }
        : {}),
    };
  }

  if (companySets !== null && companySets.length > 0) {
    return {
      type: 'company_sets',
      company_sets: companySets,
    };
  }

  return {
    type: 'company_sets',
    company_sets: ['revelio_shared'],
  };
}

function buildUnitOfAnalysisSelectionValue(
  selections: SelectionListSelectedItem[]
): DataBuilderUnitOfAnalysisSelectionInput[] {
  const groups = new Map<string, string[]>();
  for (const item of selections) {
    const existing = groups.get(item.selectionListId);
    if (existing !== undefined) {
      existing.push(item.id);
    } else {
      groups.set(item.selectionListId, [item.id]);
    }
  }
  return Array.from(groups.entries()).map(([key, value]) => ({ key, value }));
}

function buildUnitOfAnalysisPayload(
  selectedUnitOfAnalysis: string | null,
  selections: SelectionListSelectedItem[],
  companyMapper: DataBuilderCompanyMapper | null,
  companySets: DataBuilderCompanySets['company_sets'] | null
): DataBuilderUnitOfAnalysisInput {
  if (selectedUnitOfAnalysis === null || selectedUnitOfAnalysis === '') {
    return {
      type: 'company',
      value: buildCompanyUnitOfAnalysisValue(companyMapper, companySets),
    };
  }

  if (selectedUnitOfAnalysis === 'company') {
    return {
      type: 'company',
      value: buildCompanyUnitOfAnalysisValue(companyMapper, companySets),
    };
  }

  if (selectedUnitOfAnalysis === 'individual') {
    return {
      type: 'individual',
    };
  }

  return {
    type: selectedUnitOfAnalysis,
    value: buildUnitOfAnalysisSelectionValue(selections),
  };
}

function buildFiles(
  deliverable: DataBuilderDeliverable,
  columnKeysByBreakdown: Record<string, string[]>,
  customFiltersByBreakdown: Record<string, CustomFilter[]>,
  customColumnsByBreakdown: Record<string, DataBuilderCustomColumn[]>,
  includedBreakdownIds: string[]
): DataBuilderFileInput[] {
  const files = deliverable.files ?? [];
  const includedSet = new Set(includedBreakdownIds);
  return files
    .filter((file) => includedSet.has(file.key ?? ''))
    .map((file) => {
      const fileKey = file.key ?? '';
      const columnOptions = getColumnKeyOptions(file.columns);
      const selectedKeys = columnKeysByBreakdown[fileKey] ?? [];
      const preset = orderPresetColumnKeys(selectedKeys, columnOptions);
      const filters = buildFiltersFromCustomFilters(
        customFiltersByBreakdown[fileKey] ?? []
      );
      const custom = toApiCustomColumns(
        customColumnsByBreakdown[fileKey] ?? []
      );
      const base = {
        type: file.key ?? null,
        columns: { preset, custom },
      };
      return filters === null ? base : { ...base, filters };
    });
}

export type FilterPayload =
  | DataBuilderAndFilter
  | DataBuilderOrFilter
  | DataBuilderBooleanFilterInput
  | DataBuilderDateFilterInput
  | DataBuilderStringFilterInput
  | DataBuilderSelectionFilter
  | DataBuilderNumericFilterInput;
type CustomColumnStepCasePayload = NonNullable<
  NonNullable<
    DataBuilderColumnsWithCustom['custom']
  >[number]['steps'][number]['case']
>;

const NULLARY_OPERATORS = new Set(['IS_NULL', 'IS_NOT_NULL']);

function groupKeyPart(c: CustomFilterCondition): { group_key?: string } {
  if (c.groupKey === undefined || c.groupKey === null || c.groupKey === '') {
    return {};
  }
  return { group_key: c.groupKey };
}

function buildSelectionListConditions(
  c: CustomFilterCondition
): DataBuilderSelectionFilter[] {
  const operator = c.operator as DataBuilderSelectionFilter['operator'];

  // IS_NULL/IS_NOT_NULL are nullary — they require only field + operator, no values.
  if (NULLARY_OPERATORS.has(c.operator)) {
    return [
      {
        field: c.field,
        operator,
        modifier: c.modifier ?? undefined,
        ...groupKeyPart(c),
      } as DataBuilderSelectionFilter,
    ];
  }

  if (!c.selectionListItems || c.selectionListItems.length === 0) {
    return [];
  }

  const grouped = new Map<string, string[]>();
  for (const item of c.selectionListItems) {
    if (item.selectionListId === '' || item.id === '') {
      continue;
    }

    const key = item.selectionListId;
    const existing = grouped.get(key);
    if (existing) {
      existing.push(item.id);
    } else {
      grouped.set(key, [item.id]);
    }
  }

  if (grouped.size === 0) {
    return [];
  }

  return Array.from(grouped.entries()).map(([field, ids]) => ({
    field,
    operator,
    value: ids.map(Number),
    modifier: c.modifier ?? undefined,
    ...groupKeyPart(c),
  }));
}

function buildConditionPayloads(
  c: CustomFilterCondition
): (
  | DataBuilderBooleanFilterInput
  | DataBuilderDateFilterInput
  | DataBuilderStringFilterInput
  | DataBuilderSelectionFilter
  | DataBuilderNumericFilterInput
  | DataBuilderOrFilter
)[] {
  if (c.type === undefined) {
    throw new Error('buildConditionPayloads: condition.type is required');
  }

  if (c.type === 'selection_list') {
    const selectionFilters = buildSelectionListConditions(c);
    if (selectionFilters.length <= 1) {
      return selectionFilters;
    }
    // Multiple selection lists within a single condition (e.g. region + country
    // + metro_area) should be OR-combined so any matching location qualifies.
    const orGroup: DataBuilderOrFilter = {
      type: 'or',
      filters: selectionFilters,
    };
    return [orGroup];
  }
  if (c.type === 'date') {
    if (NULLARY_OPERATORS.has(c.operator)) {
      return [
        {
          field: c.field,
          operator: c.operator as DataBuilderDateFilterInput['operator'],
          modifier: c.modifier ?? undefined,
          ...groupKeyPart(c),
        } as DataBuilderDateFilterInput,
      ];
    }
    return [
      {
        field: c.field,
        operator: c.operator as DataBuilderDateFilterInput['operator'],
        value: c.value,
        modifier: c.modifier ?? undefined,
        ...groupKeyPart(c),
      },
    ];
  }
  if (c.type === 'numeric') {
    if (NULLARY_OPERATORS.has(c.operator)) {
      return [
        {
          field: c.field,
          operator: c.operator as DataBuilderNumericFilterInput['operator'],
          modifier: c.modifier ?? undefined,
          ...groupKeyPart(c),
        } as DataBuilderNumericFilterInput,
      ];
    }
    return [
      {
        field: c.field,
        operator: c.operator as DataBuilderNumericFilterInput['operator'],
        value: c.value.map(Number),
        modifier: c.modifier ?? undefined,
        ...groupKeyPart(c),
      },
    ];
  }
  if (c.type === 'boolean') {
    return [
      {
        field: c.field,
        operator: c.operator as DataBuilderBooleanFilterInput['operator'],
        modifier: c.modifier ?? undefined,
        ...groupKeyPart(c),
      },
    ];
  }
  if (NULLARY_OPERATORS.has(c.operator)) {
    return [
      {
        field: c.field,
        operator: c.operator as DataBuilderStringFilterInput['operator'],
        modifier: c.modifier ?? undefined,
        ...groupKeyPart(c),
      } as DataBuilderStringFilterInput,
    ];
  }
  return [
    {
      field: c.field,
      operator: c.operator as DataBuilderStringFilterInput['operator'],
      value: c.value,
      modifier: c.modifier ?? undefined,
      ...groupKeyPart(c),
    },
  ];
}

export function buildCustomFilterPayload(
  filter: CustomFilter
): FilterPayload | null {
  const conditions = filter.conditions.flatMap(buildConditionPayloads);

  if (conditions.length === 0) {
    return null;
  }

  if (conditions.length === 1) {
    const condition = conditions[0];
    if (condition === undefined) return null;
    // If the single condition is already a compound filter (e.g. a multi-key
    // selection list OR group), attach the filter name to it.
    if (filter.name && 'type' in condition && 'filters' in condition) {
      return { ...condition, name: filter.name } as FilterPayload;
    }
    // Wrap single leaf conditions in an AND group to preserve the filter name
    // so it survives a serialization round-trip (leaf filters don't carry a
    // name field).
    if (filter.name) {
      return {
        name: filter.name,
        type: 'and',
        filters: [condition],
      } as DataBuilderAndFilter;
    }
    return condition;
  }

  const wrapType = filter.binary === 'OR' ? 'or' : 'and';
  return {
    name: filter.name || undefined,
    type: wrapType,
    filters: conditions,
  } as DataBuilderAndFilter | DataBuilderOrFilter;
}

function buildCustomColumnStepCase(
  stepCase: CustomFilter | null
): CustomColumnStepCasePayload | null {
  if (stepCase === null || stepCase.conditions.length === 0) {
    return null;
  }

  const payload = buildCustomFilterPayload(stepCase);
  if (payload === null) {
    return null;
  }

  return payload;
}

function toApiCustomColumns(
  columns: DataBuilderCustomColumn[]
): DataBuilderColumnsWithCustom['custom'] {
  if (columns.length === 0) {
    return null;
  }

  return columns.map((column) => ({
    name: column.name,
    steps: column.steps.map((step) => ({
      case: buildCustomColumnStepCase(step.case),
      then: step.then,
    })),
  }));
}

function buildFiltersFromCustomFilters(
  customFilters: CustomFilter[]
): FilterPayload | null {
  const parts: FilterPayload[] = [];

  for (const cf of customFilters) {
    const payload = buildCustomFilterPayload(cf);
    if (payload !== null) {
      parts.push(payload);
    }
  }

  if (parts.length === 0) return null;
  if (parts.length === 1) {
    const part = parts[0];
    return part ?? null;
  }
  // Multiple top-level custom filters are AND-combined.
  return { type: 'and', filters: parts };
}

function buildFilters(
  instanceConfig: DatasetInstanceConfig
): FilterPayload | null {
  return buildFiltersFromCustomFilters(instanceConfig.customFilters ?? []);
}

function buildSettings(
  definitions: DataBuilderDeliverableSetting[] | undefined,
  selectedSettings: Record<string, DataBuilderSettingValue>
): Record<string, string | boolean> | undefined {
  if (!Array.isArray(definitions) || definitions.length === 0) {
    return undefined;
  }

  const entries = definitions.map((setting) => {
    const selectedValue = selectedSettings[setting.key];

    if (setting.type === 'toggle') {
      const value =
        typeof selectedValue === 'boolean' ? selectedValue : setting.default;
      return [setting.key, value] as const;
    }

    const value =
      typeof selectedValue === 'string' &&
      setting.items.some((item) => item.value === selectedValue)
        ? selectedValue
        : setting.default;
    return [setting.key, value] as const;
  });

  return Object.fromEntries(entries);
}

function buildDeliverableInput(
  deliverable: DataBuilderDeliverable,
  datasetInstanceLabel: string,
  instanceConfig: DatasetInstanceConfig,
  payloadState: BuildPayloadState,
  modelVersions: ModelVersionSelections
): DataBuilderDeliverableInput {
  const delivery = buildDelivery(payloadState);
  const aggregation = buildAggregation(
    deliverable,
    instanceConfig.columnKeysByBreakdown,
    instanceConfig.customColumnsByBreakdown
  );
  const files = buildFiles(
    deliverable,
    instanceConfig.columnKeysByBreakdown,
    instanceConfig.customFiltersByBreakdown,
    instanceConfig.customColumnsByBreakdown,
    instanceConfig.includedBreakdownIds
  );
  const unitOfAnalysis = buildUnitOfAnalysisPayload(
    instanceConfig.unitOfAnalysis,
    instanceConfig.unitOfAnalysisSelections ?? [],
    instanceConfig.companyMapper,
    instanceConfig.companySets
  );
  const filters = buildFilters(instanceConfig);
  const settings = buildSettings(deliverable.settings, instanceConfig.settings);
  const models = modelVersions as DataBuilderModelVersions;
  const basePayload = {
    type: deliverable.key,
    name: datasetInstanceLabel,
    models,
    delivery,
    unit_of_analysis: unitOfAnalysis,
    filters,
    aggregation: aggregation ?? undefined,
    files,
  };

  const payloadWithSettings =
    settings === undefined ? basePayload : { ...basePayload, settings };

  return payloadWithSettings;
}

const DEFAULT_INSTANCE_CONFIG: DatasetInstanceConfig = {
  settings: {},
  unitOfAnalysis: null,
  unitOfAnalysisSelections: [],
  uoaSetAt: null,
  columnKeysByBreakdown: {},
  companySelections: {},
  companySets: null,
  companyMapper: null,
  customFilters: [],
  customFiltersByBreakdown: {},
  customColumnsByBreakdown: {},
  includedBreakdownIds: [],
};

export function buildVerifyPayload(
  input: BuildPayloadInput
): DataBuilderPayload {
  const { schema, state, client, name, datasets } = input;
  const all = flattenDeliverables(schema.deliverables ?? []);
  const datasetLabelsByKey = buildDatasetLabelMap(datasets);
  const datasetInstanceLabels = buildDatasetInstanceLabelMap(
    state.datasetInstances,
    datasetLabelsByKey
  );

  const deliverables = state.datasetInstances
    .map((instance) => {
      const deliverable = all.find((d) => d.key === instance.datasetKey);
      if (deliverable === undefined) return undefined;
      const config =
        state.configByInstance[instance.instanceId] ?? DEFAULT_INSTANCE_CONFIG;
      const datasetInstanceLabel =
        datasetInstanceLabels[instance.instanceId] ?? instance.datasetKey;
      return buildDeliverableInput(
        deliverable,
        datasetInstanceLabel,
        config,
        state,
        state.modelVersions
      );
    })
    .filter((d): d is DataBuilderDeliverableInput => d !== undefined);

  return {
    client,
    name,
    deliverables,
    settings: { lag_data: state.lagData },
  };
}
